import React, { useEffect, useRef, useState } from "react";

import {
  faCalculatorSimple,
  faCode,
  faCopy,
  faEdit,
  faEye,
  faHistory,
  faInfoCircle,
  faMinus,
  faPlus,
  faToggleOff,
  faToggleOn,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";
import { noop } from "lodash-es";
import { useSearchParams } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { HighlightedText } from "src/base-components/HighlightedText";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import {
  FeatureQueryEnvironmentSetting,
  FeatureQueryStatus,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { EntityFilter } from "src/featureCatalogue/Features/FilterActions";
import { BackfillQueryModal } from "src/featureCatalogue/Queries/BackfillQueryModal";
import { ManageQueryModal } from "src/featureCatalogue/Queries/ManageQueryModal";
import { QueryStatusPill } from "src/featureCatalogue/Queries/QueryStatusPill";
import { QueryToggleConfirmationModal } from "src/featureCatalogue/Queries/QueryToggleConfirmationModal";
import {
  useFeatureQueries,
  useFeatureQuery,
  useSetFeatureQueryStatus,
  useUpdateFeatureQuery,
} from "src/featureCatalogue/queries";
import {
  commonColumns,
  EntityPills,
  formatAthenaSqlQuery,
  isActiveQuery,
  Query,
  statusColumns,
} from "src/featureCatalogue/utils";
import { ExcludesFalse } from "src/flow/types";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  formatDate,
  formatDateWithThreshold,
  parseDateIfNeeded,
} from "src/utils/datetime";
import { HighlightedSql } from "src/utils/highlighter";
import { getTypeIcon } from "src/utils/icons";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { errorMessage } from "src/utils/stringUtils";
import { useShowOnHover } from "src/utils/useShowOnHover";

const columnHelper = createColumnHelper<Query>();

const QUERY_VIEW_COLUMN = [
  columnHelper.accessor((row) => row.query_name || "Untitled Feature Query", {
    id: "query_name",
    header: (info) => <Header info={info}>Query Name</Header>,
    cell: (info) => <DisplaySqlCell info={info} />,
    size: 300,
    maxSize: 400,
    filterFn: "includesString",
    enableGlobalFilter: true,
    meta: { sticky: "left" },
  }),
  columnHelper.accessor(
    (row) =>
      row.columns.features?.map((feature) => feature.feature_key).join(" ") ??
      "",
    {
      id: "feature_key",
      header: (info) => <Header info={info}>Feature Key</Header>,
      cell: (info) => {
        const features = info.row.original.columns.features ?? [];

        return (
          <Cell className="h-auto" info={info}>
            <div className="flex min-h-fit flex-col items-start space-y-2">
              {features?.map((feature) => (
                <Tooltip
                  key={feature.feature_key}
                  delay={250}
                  headerAction={
                    <CopyTextIcon
                      color="text-white hover:text-gray-500"
                      feedback="inline-only-checkmark"
                      value={feature.feature_key}
                    />
                  }
                  placement="top"
                  title={feature.feature_key}
                  asChild
                >
                  <Pill size="sm" variant="gray">
                    <Pill.Icon
                      icon={getTypeIcon(feature.feature_type ?? "", faMinus)}
                    />
                    <Pill.Text>
                      <HighlightedText
                        highlight={info.table.getState().globalFilter}
                        highlightClassName="font-inter-semibold-12px bg-yellow-200"
                        text={feature.feature_key}
                      />
                    </Pill.Text>
                  </Pill>
                </Tooltip>
              ))}
            </div>
          </Cell>
        );
      },
      size: 180,
      maxSize: 280,
      filterFn: "includesString",
      enableGlobalFilter: true,
    },
  ),
  columnHelper.accessor(
    (row) =>
      row.columns.entities?.map((entity) => entity.entity_name).join(" ") ?? "",
    {
      id: "entities",
      header: (info) => (
        <Header info={info}>
          <div className="flex w-full justify-between gap-x-8">
            <span>Entity</span>
            <div className="flex gap-x-1">
              <EntityFilter column={info.column} type="query" />
            </div>
          </div>
        </Header>
      ),
      minSize: 150,
      maxSize: 150,
      cell: (info) => {
        const entities = info.row.original.columns.entities;
        return (
          <Cell info={info}>
            <EntityPills entities={entities?.map((e) => e.entity_name) ?? []} />
          </Cell>
        );
      },
      filterFn: "includesString",
      enableSorting: true,
      enableGlobalFilter: true,
    },
  ),
  ...commonColumns<Query>(),
  // We show the first feature's default value in the query list view
  columnHelper.accessor((row) => row.columns.features?.[0]?.feature_default, {
    id: "feature_default",
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-2">
          <span className="whitespace-nowrap">Default Value</span>
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>{speakPythonPrimitive(info.getValue() ?? "None")}</Cell>
    ),
    enableSorting: true,
  }),
  ...statusColumns(columnHelper, {
    getEnvironmentSettings: (row) => row.environment_settings,
    hasQuery: (row) => !!row?.id,
  }),
  columnHelper.display({
    id: "actions",
    header: (info) => <Header info={info} />,
    cell: (info) => (
      <Cell info={info}>
        <QueryActionsMenu
          query={info.row.original}
          onBackfillQuery={info.table.options.meta?.actions?.onBackfillQuery}
          onDuplicateQuery={info.table.options.meta?.actions?.onDuplicateQuery}
          onEditQuery={info.table.options.meta?.actions?.onEditQuery}
          onSetStatusQuery={info.table.options.meta?.actions?.onSetStatusQuery}
        />
      </Cell>
    ),
    size: 130,
    meta: { sticky: "right" },
  }),
];

const COLUMNS = [
  columnHelper.accessor("id", {
    header: (info) => <Header info={info}>Query ID</Header>,
    cell: (info) => (
      <Cell className="w-24" info={info}>
        <TruncatedIdPill id={info.getValue()} />
      </Cell>
    ),
    minSize: 80,
    maxSize: 80,
  }),
  columnHelper.accessor("query", {
    header: (info) => <Header info={info}>SQL Query</Header>,
    cell: (info) => <DisplaySqlCell info={info} />,
    size: 300,
    maxSize: 400,
  }),
  columnHelper.accessor("environment_settings", {
    header: (info) => <Header info={info}>Status</Header>,
    cell: (info) => {
      const environmentSettings = info.getValue();

      return (
        <Cell info={info}>
          <StatusPill environmentSettings={environmentSettings} />
        </Cell>
      );
    },
    size: 100,
  }),
  columnHelper.accessor("columns.features", {
    header: (info) => <Header info={info}>Features</Header>,
    cell: (info) => {
      const features = info.getValue() ?? [];
      const [displayFeatures, remainingFeatures] =
        features.length > 2
          ? [features.slice(0, 1), features.slice(1)]
          : [features, []];

      return (
        <Cell info={info}>
          <div className="flex gap-2">
            {displayFeatures?.map((feature) => (
              <Tooltip
                delay={250}
                headerAction={
                  <CopyTextIcon
                    color="text-white hover:text-gray-500"
                    feedback="inline-only-checkmark"
                    value={feature.feature_key}
                  />
                }
                placement="top"
                title={feature.feature_key}
                asChild
              >
                <Pill key={feature.feature_key} size="sm" variant="gray">
                  <Pill.Icon
                    icon={getTypeIcon(feature.feature_type ?? "", faMinus)}
                  />
                  <Pill.Text>
                    <span
                      className={twJoin(
                        "block truncate",
                        displayFeatures.length === 1 ? "max-w-40" : "max-w-16",
                      )}
                    >
                      {feature.feature_key}
                    </span>
                  </Pill.Text>
                </Pill>
              </Tooltip>
            ))}
            {remainingFeatures?.length > 0 && (
              <RemainingFeaturesPill features={remainingFeatures} />
            )}
          </div>
        </Cell>
      );
    },
    size: 180,
    maxSize: 280,
  }),
  columnHelper.accessor("columns.entities", {
    header: (info) => <Header info={info}>Entity</Header>,
    cell: (info) => (
      <Cell className="flex space-x-2" info={info}>
        <EntityPills
          entities={info.getValue()?.map((e) => e.entity_name) ?? []}
        />
      </Cell>
    ),
  }),
  ...commonColumns<Query>(),
  columnHelper.accessor("created_at", {
    header: (info) => <Header info={info}>Created at</Header>,
    cell: (info) => (
      <Cell info={info}>
        <RelativeOrAbsoluteDate
          date={info.getValue()}
          format="d MMM yyyy h:mm aaa"
          thresholdDays={30}
        />
      </Cell>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: (info) => <Header info={info} />,
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <QueryActionsMenu
          query={info.row.original}
          onBackfillQuery={info.table.options.meta?.actions?.onBackfillQuery}
          onDuplicateQuery={info.table.options.meta?.actions?.onDuplicateQuery}
          onEditQuery={info.table.options.meta?.actions?.onEditQuery}
          onSetStatusQuery={info.table.options.meta?.actions?.onSetStatusQuery}
        />
      </Cell>
    ),
    size: 40,
  }),
];

export const Queries: React.FC<{ searchQuery?: string }> = ({
  searchQuery,
}) => {
  const {
    openModal,
    closeModal,
    afterLeave,
    isOpen,
    data: queryToEdit,
  } = useModal<Query | Omit<Query, "id">>();
  const {
    openModal: openBackfillModal,
    closeModal: closeBackfillModal,
    afterLeave: afterLeaveBackfill,
    isOpen: isBackfillModalOpen,
    data: queryToBackfill,
  } = useModal<Query>();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const { data, isLoading, isSuccess } = useFeatureQueries();
  const { mutate: updateFeatureQuery } = useUpdateFeatureQuery({
    onError: (error) => {
      toastActions.failure({
        title: "Failed to update feature query",
        description: error.response?.data.detail ?? TAKTILE_TEAM_NOTIFIED,
      });
    },
  });
  const { value: isDuplicateModal, toggle: toggleIsDuplicateModal } =
    useBoolean(false);

  const { mutateAsync: setFeatureQueryStatus } = useSetFeatureQueryStatus();

  useOpenEditQueryModalFromParams(openModal);

  return (
    <WhitePane>
      <div className="flex h-full w-full flex-col">
        {isSuccess && (data ?? []).length === 0 ? (
          <EmptyState
            actionButton={{
              icon: faPlus,
              text: "Create Feature Query",
              onClick: () => {
                openModal();
              },
            }}
            dataLoc="queries-empty-state"
            description="Create Feature Queries"
            headline="No Feature Queries found"
            icon={faCode}
          />
        ) : (
          <div
            className={`${featureLifecycleEnabled ? "relative flex h-full w-full flex-col p-4" : ""}`}
          >
            <TableComp
              actions={{
                onEditQuery: openModal,
                onChangeQueryStatus: updateFeatureQuery,
                onBackfillQuery: openBackfillModal,
                onDuplicateQuery: ({ id: _dropId, ...query }: Query) => {
                  toggleIsDuplicateModal();
                  openModal({
                    ...query,
                    columns: {
                      ...query.columns,
                      features: [
                        {
                          column_name: "",
                          feature_key: "",
                          feature_type: undefined,
                          feature_default: "None",
                          evaled_feature_default: null,
                        },
                      ],
                    },
                  });
                },
                onSetStatusQuery: setFeatureQueryStatus,
              }}
              columns={featureLifecycleEnabled ? QUERY_VIEW_COLUMN : COLUMNS}
              data={
                (data?.filter((item): item is Query => item.id !== undefined) ??
                  []) as Query[]
              }
              dataLoc="queries-list"
              frameClassName={`w-full ${featureLifecycleEnabled ? "" : "p-4"}`}
              globalFilter={featureLifecycleEnabled ? searchQuery : undefined}
              isLoading={isLoading}
              rowPropsGetter={(row) => ({
                classNameOverrides: `${featureLifecycleEnabled ? "[&>td]:align-top [&>td.sticky]:z-10 [&>td.sticky]:bg-white" : ""} `,
                "data-loc": row.original.columns.features?.[0]?.feature_key,
              })}
              variant="compact"
              clientSideFiltering
            />
          </div>
        )}
      </div>
      <ManageQueryModal
        afterLeave={afterLeave}
        open={isOpen}
        query={queryToEdit}
        readOnly={isDuplicateModal ? false : !!queryToEdit?.finalized_at}
        onClose={() => {
          closeModal();
          if (isDuplicateModal) {
            toggleIsDuplicateModal();
          }
        }}
      />
      <BackfillQueryModal
        afterLeave={afterLeaveBackfill}
        open={isBackfillModalOpen}
        query={queryToBackfill}
        onClose={closeBackfillModal}
      />
    </WhitePane>
  );
};

const DisplaySqlCell: React.FC<{ info: CellContext<Query, string> }> = ({
  info,
}) => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const [isOpen, setIsOpen] = useState(false);
  const queryName = info.row.original.query_name || "Untitled Feature Query";
  const sqlQuery = info.row.original.query;

  const renderCodeEditor = () => {
    return (
      <CodeEditor
        language="sql"
        value={formatAthenaSqlQuery(sqlQuery)}
        lineWrapping
        readOnly
      />
    );
  };

  return (
    <CustomPopover
      button={
        <Cell info={info}>
          {featureLifecycleEnabled ? (
            <HighlightedText
              highlight={info.table.getState().globalFilter}
              highlightClassName="font-inter-semibold-12px bg-yellow-200"
              text={queryName}
            />
          ) : (
            <HighlightedSql sqlString={sqlQuery} />
          )}
        </Cell>
      }
      className="h-[400px] w-[400px] border-0"
      isOpen={isOpen}
      offsetX={0}
      placement={featureLifecycleEnabled ? "right-start" : "top"}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen &&
        (featureLifecycleEnabled ? (
          <div className="flex h-full flex-col rounded-lg px-4 py-2">
            <span className="mb-2 font-inter-semibold-12px">{queryName}</span>
            {renderCodeEditor()}
          </div>
        ) : (
          renderCodeEditor()
        ))}
    </CustomPopover>
  );
};

const QueryActionsMenu: React.FC<{
  query: Query;
  onEditQuery: ((query: Query) => void) | undefined;
  onDuplicateQuery: ((query: Query) => void) | undefined;
  onBackfillQuery: ((query: Query) => void) | undefined;
  onSetStatusQuery:
    | ((params: {
        status: FeatureQueryStatus;
        environment: PreviewQueryRequestEnvironmentEnum;
        id: string;
        etag: string;
      }) => void)
    | undefined;
}> = ({
  query,
  onEditQuery,
  onDuplicateQuery,
  onBackfillQuery,
  onSetStatusQuery,
}) => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const { openModal: openConfirmationModal, isOpen, closeModal } = useModal();
  const [environment] = useEnvironment();
  const envEnum =
    environment === "sandbox"
      ? PreviewQueryRequestEnvironmentEnum.SANDBOX
      : PreviewQueryRequestEnvironmentEnum.LIVE;

  const isActive = isActiveQuery(query, envEnum);

  const setQueryStatus = async () => {
    try {
      await onSetStatusQuery?.({
        id: query.id,
        environment: envEnum,
        status: isActiveQuery(query, envEnum)
          ? FeatureQueryStatus.INACTIVE
          : FeatureQueryStatus.ACTIVE,
        etag: query.etag,
      });

      toastActions.success({
        title: isActive ? "Query Deactivated" : "Query Activated",
        description: isActive
          ? "Query is successfully deactivated."
          : "Query is successfully activated.",
      });
    } catch (error) {
      const message = errorMessage(error);
      const toastId = toastActions.failure({
        title: isActive ? "Deactivation Failed" : "Activation Failed",
        description: isActive
          ? message || "Query couldn't be deactivated. Try again."
          : message || "Query couldn't be activated. Try again.",
        actionText: message ? undefined : "Try again",
        onActionClick: () => {
          toastActions.dismiss(toastId);
          setQueryStatus();
        },
      });
    }
  };

  const { featureQueriesCatalogue } = useCapabilities();

  if (featureLifecycleEnabled) {
    const options = [
      {
        icon: faEye,
        action: () => onEditQuery?.(query),
        title: "View Query",
        canAccess: featureQueriesCatalogue.canEdit,
      },
      {
        icon: faTrashCan,
        action: noop,
        title: "Archive",
        canAccess: true,
      },
      {
        icon: faHistory,
        action: () => onBackfillQuery?.(query),
        title: "Backfill",
        canAccess: featureQueriesCatalogue.canBackfill,
      },
      {
        icon: faCopy,
        action: () => onDuplicateQuery?.(query),
        title: "Duplicate",
        canAccess: featureQueriesCatalogue.canDuplicate,
      },
    ];
    return (
      <>
        <div className="flex cursor-pointer items-center gap-x-3 opacity-0 transition-opacity group-hover:opacity-100">
          {options
            .filter((option) => option.canAccess !== false)
            .map((option) => (
              <Tooltip key={option.title} title={option.title}>
                <Icon
                  color="text-gray-500"
                  icon={option.icon}
                  size="xs"
                  onClick={option.action}
                />
              </Tooltip>
            ))}
        </div>
        <QueryToggleConfirmationModal
          closeModal={closeModal}
          isActive={isActive}
          isOpen={isOpen}
          onConfirm={() => {
            closeModal();
            setTimeout(setQueryStatus, 100);
          }}
        />
      </>
    );
  }

  const menuElements = [
    featureQueriesCatalogue.canActivate && {
      key: isActive ? "Deactivate query" : "Activate query",
      icon: isActive ? faToggleOff : faToggleOn,
      action:
        envEnum === PreviewQueryRequestEnvironmentEnum.LIVE
          ? openConfirmationModal
          : setQueryStatus,
      suffix:
        envEnum === PreviewQueryRequestEnvironmentEnum.SANDBOX ? (
          <Tooltip
            body="This query will be activated only in Sandbox environment."
            placement="top-end"
            asChild
          >
            <Icon color="text-gray-500" icon={faInfoCircle} size="sm" />
          </Tooltip>
        ) : undefined,
    },
    featureQueriesCatalogue.canEdit && {
      key: "Edit Query",
      icon: faEdit,
      action: () => onEditQuery?.(query),
    },
    featureQueriesCatalogue.canBackfill && {
      key: "Backfill",
      icon: faHistory,
      action: () => onBackfillQuery?.(query),
    },
    featureQueriesCatalogue.canDuplicate && {
      key: "Duplicate",
      icon: faCopy,
      action: () => onDuplicateQuery?.(query),
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  return (
    <>
      <EllipsisOptionsDropdown
        buttonClassName="text-gray-500 ml-1 group-hover/row:opacity-100 opacity-0"
        dataLoc="query-actions-menu-button"
        elements={menuElements}
        iconSize="xs"
        placement="bottom end"
        timeoutDuration={200}
      />
      <QueryToggleConfirmationModal
        closeModal={closeModal}
        isActive={isActive}
        isOpen={isOpen}
        onConfirm={() => {
          closeModal();
          setTimeout(setQueryStatus, 100);
        }}
      />
    </>
  );
};

const useOpenEditQueryModalFromParams = (openModal: (query: Query) => void) => {
  const hasBeenOpened = useRef(false);
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("queryId");
  const { data: queryParams } = useFeatureQuery(queryId ?? "");

  // Open the modal with the query data when the queryId is in the search params
  useEffect(() => {
    if (queryParams?.id && !hasBeenOpened.current) {
      openModal(queryParams as Query);
      hasBeenOpened.current = true;
    }
  }, [openModal, queryParams]);
};

const RemainingFeaturesPill: React.FC<{
  features: { feature_key: string }[];
}> = ({ features }) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useShowOnHover(700);

  return (
    <CustomPopover
      button={
        <span className="cursor-default">
          <Pill size="sm" variant="gray">
            <Pill.Icon icon={faPlus} />
            <Pill.Text>{features.length} more</Pill.Text>
          </Pill>
        </span>
      }
      className="flex flex-wrap justify-start gap-2 p-4"
      isOpen={isHovered}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {features.map((feature) => (
        <Pill key={feature.feature_key} size="sm" variant="gray">
          <Pill.Icon icon={faCalculatorSimple} />
          <Pill.Text>{feature.feature_key}</Pill.Text>
        </Pill>
      ))}
    </CustomPopover>
  );
};

const RelativeOrAbsoluteDate: React.FC<{
  date: string;
  format: string;
  thresholdDays: number;
}> = ({ date, thresholdDays = 30, format }) => {
  const parsedDate = parseDateIfNeeded(date);
  const diffInDays = differenceInDays(new Date(), parsedDate);

  const dateString = formatDateWithThreshold(date, thresholdDays, format);

  return (
    <Tooltip
      body={formatDate(date, format)}
      disabled={diffInDays >= thresholdDays}
      placement="top-end"
      asChild
    >
      <span>{dateString}</span>
    </Tooltip>
  );
};

const StatusPill = ({
  environmentSettings,
}: {
  environmentSettings: Record<string, FeatureQueryEnvironmentSetting>;
}) => {
  const [environment] = useEnvironment();

  const status = environmentSettings[environment]?.status;

  if (!status) {
    return null;
  }

  return <QueryStatusPill status={status} />;
};
